import React, { useEffect, useMemo, useState } from 'react'
import getDataFromApi from '../../../../components/getDataFromApi'
import { CheckCircleIcon, CircleTimeIcon, FailedIcon, LinkIcon, QueuedIcon } from '../../../Common/SvgIcons/WorkflowIcons'
import { useNavigate } from 'react-router-dom'
import { CheckIcon } from 'lucide-react';

const STATUS_CLASS_MAP = {
    complete: "recent-log-card__badge--success",
    completed: "recent-log-card__badge--success",
    failed: "recent-log-card__badge--danger",
    terminated: "recent-log-card__badge--danger",
    queued: "recent-log-card__badge--info",
    quued: "recent-log-card__badge--info",
    evaluating: "recent-log-card__badge--info",
    running: "recent-log-card__badge--info",
};

const formatLabel = (value = "") =>
    String(value || "")
        .replace(/_/g, " ")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (field, value) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "object") return "";
    if (field === "execution_time") return String(value).split(".")[0];
    if (field === "section" || field === "status") return formatLabel(value);
    return String(value);
};

const getLogDate = (row) =>
    row?.created ||
    row?.created_at ||
    row?.updated_at ||
    row?.timestamp ||
    row?.time ||
    row?.date ||
    "";

const formatRelativeTime = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return "just now";

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
};

const getStatus = (row) =>
    String(row?.status || row?.state || row?.evaluation_status || row?.run_status || "").toLowerCase();

const getStatusIcon = (status) => {
    if (["complete", "completed"].includes(status)) {
        return <CheckIcon className="wf-icon-small" />;
    }

    if (["failed", "terminated"].includes(status)) {
        return <FailedIcon className="wf-icon-small" />;
    }

    if (["queued", "quued", "evaluating", "running"].includes(status)) {
        return <QueuedIcon className="wf-icon-small" />;
    }

    return <CircleTimeIcon className="wf-icon-small" />;
};

const getTitle = (row, viewFrom) => {
    if (viewFrom === "Admin") {
        return row?.tenant_name || row?.organization_name || row?.tenant || row?.name || `Tenant ${row?.tenantId || ""}`.trim();
    }

    return (
        row?.model_tested ||
        row?.model_name ||
        row?.operation ||
        row?.name ||
        row?.section ||
        "Operation recorded"
    );
};

const getMeta = (row, viewFrom) => {
    const fields = viewFrom === "Admin"
        ? ["total_evaluations", "evaluations", "total_count", "completed", "complete", "failed", "running"]
        : ["section", "created_by", "device", "status", "execution_time"];

    return fields
        .map((field) => {
            const value = formatValue(field, row?.[field]);
            return value ? `${formatLabel(field)}: ${value}` : "";
        })
        .filter(Boolean)
        .slice(0, 3)
        .join(" · ");
};

const normalizeRows = (response, viewFrom, tenantId) => {
    if (!response || typeof response !== "object") return [];

    if (viewFrom === "Admin") {
        return Object.entries(response).map(([id, value]) => ({
            id,
            tenantId: id,
            ...(value || {}),
        }));
    }

    const stats = response?.[tenantId]?.user_level_stats || {};

    return Object.entries(stats).map(([id, value]) => ({
        id,
        ...(value || {}),
    }));
};

export const RecentLogs = ({ className = "", viewFrom, tenantId, userName, limit = 4, isOwnProfile = false }) => {

    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const role = localStorage.getItem("role");
    const hasTenantIdProp = tenantId !== undefined && tenantId !== null;
    const hasUserNameProp = userName !== undefined && userName !== null;
    const effectiveTenantId = hasTenantIdProp ? String(tenantId || "") : localStorage.getItem("tenant_id") || "";
    const effectiveUserName = hasUserNameProp ? String(userName || "") : localStorage.getItem("name") || "";
    const isDarkCard = className.includes("text-white") || className.includes("bg-main");

    useEffect(() => {
        let cancelled = false;

        const fetchPreview = async () => {
            if ((viewFrom === "Tenant" || viewFrom === "User") && !effectiveTenantId) {
                setRows([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            setHasError(false);

            const filters = viewFrom === "Admin"
                ? {
                    level: role || "system_admin",
                    table_type: "main_table",
                }
                : {
                    level: viewFrom === "Tenant" ? "tenant_admin" : "user",
                    table_type: "sub_table",
                    tenant_id: effectiveTenantId,
                    ...(viewFrom === "User" && effectiveUserName && { created_by: effectiveUserName }),
                };

            try {
                const response = await getDataFromApi(
                    `${window.ENV.API_URL}/models?filters=${encodeURIComponent(
                        JSON.stringify(filters)
                    )}&section=evaluation_stats`,
                    "get"
                );

                if (cancelled) return;

                const nextRows = normalizeRows(response, viewFrom, effectiveTenantId)
                    .sort((a, b) => new Date(getLogDate(b) || 0) - new Date(getLogDate(a) || 0));

                setRows(nextRows);
            } catch (error) {
                console.error("Failed to fetch recent operations:", error);
                if (!cancelled) {
                    setRows([]);
                    setHasError(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchPreview();

        return () => {
            cancelled = true;
        };
    }, [effectiveTenantId, effectiveUserName, limit, role, viewFrom]);

    const previewRows = useMemo(() => rows.slice(0, limit), [rows, limit]);

    const openHistory = () =>
        navigate("/admin/history", {
            state: {
                viewFrom,
                isOwnProfile: Boolean(isOwnProfile),
            },
        });

    return (
        <>

            <section className={`container-card recent-log-card ${isDarkCard ? "recent-log-card--dark" : ""} ${className}`}>
                <div className='d-a justify-content-between gap-2'>
                    <div className='d-a gap-2 flex-fill'>
                        <CircleTimeIcon className="wf-icon recent-log-card__headerIcon" />
                        <div>
                            <h6 className="m-0">Recent Operations Log</h6>
                            <small className="recent-log-card__subtext">Latest audit activity</small>
                        </div>
                    </div>
                    <button className='plain-button recent-log-card__viewAll' onClick={openHistory}>View all</button>
                </div>

                <div className="recent-log-card__body">
                    {loading ? (
                        <div className="recent-log-card__empty">Loading recent activity...</div>
                    ) : hasError ? (
                        <div className="recent-log-card__empty">Recent activity is unavailable right now.</div>
                    ) : previewRows.length === 0 ? (
                        <div className="recent-log-card__empty">
                            No recent operations found.
                        </div>
                    ) : (
                        <div className="recent-log-card__list">
                            {previewRows.map((row, index) => {
                                const status = getStatus(row);
                                const statusLabel = status ? formatLabel(status) : "";
                                const badgeClass = STATUS_CLASS_MAP[status] || "recent-log-card__badge--neutral";
                                const relativeTime = formatRelativeTime(getLogDate(row));
                                const meta = getMeta(row, viewFrom);

                                return (
                                    <button
                                        type="button"
                                        className="recent-log-card__row"
                                        key={row.id || row.tenantId || index}
                                        onClick={openHistory}
                                    >
                                        <span className={`recent-log-card__rowIcon ${badgeClass}`}>
                                            {getStatusIcon(status)}
                                        </span>

                                        <span className="recent-log-card__rowContent">
                                            <strong>{getTitle(row, viewFrom)}</strong>
                                            <small>{meta || "Activity recorded in audit history"}</small>
                                        </span>

                                        <span className="recent-log-card__side">
                                            {statusLabel && <span className={`recent-log-card__badge ${badgeClass}`}>{statusLabel}</span>}
                                            {relativeTime && <small>{relativeTime}</small>}
                                            <LinkIcon className="wf-icon-small recent-log-card__linkIcon" />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

        </>
    )
}
